import * as React from "react";
import {
    ClientStatus,
    IClient,
    IFeesCalculated,
    IFeeSettingFields,
} from "intake_shared/src/types";
import styles from "./FeesForm.module.scss";
import {
    TUpdateClient,
    TUpdateClientByStatus,
    TUpdateMultipleClientFieldsByStatus,
} from "../../../clientUiTypes";
import { AclContext } from "../../../../../../contexts/AclContext";
import { FormCheckbox } from "../../../../bits/FormCheckbox/FormCheckbox";
import { TextArea } from "../../../../bits/TextArea/TextArea";
import Table from "../../../../bits/Table/Table";
import { getClinicianByEmail } from "../../../../../../store/clinician";
import FeeInputWithTooltip from "../FeeInputWithTooltip/FeeInputWithTooltip";
import { validateFeeFormat, validateFees } from "../../../../../../utils/utils";
import Alert from "../../../../bits/Alert/Alert";
import { Button } from "../../../../bits/Button/Button";
import ClinicianFeesDialog from "../ClinicianFeesDialog/ClinicianFeesDialog";
import PlusCircleIcon from "../../../../icons/PlusCircle";
import { cn } from "../../../../../helpers";
import { useToasts } from "react-toast-notifications";
import {
    buildSelfPayAutoFillToastMessage,
    getSelfPayInsurancePatch,
} from "../../../../../v2utils";

// Function to validate all fees in a client
export const validateAllFees = (
    client: IClient
): { isValid: boolean; invalidFees: string[] } => {
    const feeFields = [
        "masters_fee",
        "dr_fee",
        "dr_fee_a",
        "dr_fee_b",
        "dr_fee_c",
        "psychiatry_fee",
        "psychiatry_fee_a",
        "psychiatry_fee_b",
        "psychiatry_fee_c",
        "intern_fee",
        "lic_fee",
        "kap_fee",
        "lic_a_fee",
        "lic_b_fee",
        "lic_c_fee",
        "extern_fee",
        "career_fee",
        "gifted_assessment_fee",
        "short_term_evaluation_documentation_fee",
        "nutrition_fee",
        "inn_masters_fee",
        "inn_nutrition_fee",
    ];

    const invalidFees: string[] = [];

    // Validate general fees
    feeFields.forEach((field) => {
        const feeValue = client[ClientStatus.FEE_SETTING]?.[field];
        if (feeValue && !validateFeeFormat(feeValue)) {
            invalidFees.push(
                `General ${field
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}`
            );
        }
    });

    // Validate clinician-specific fees
    if (client.clinician_fees) {
        Object.entries(client.clinician_fees).forEach(([clinicianEmail, fees]) => {
            feeFields.forEach((field) => {
                const feeValue = fees[field];
                if (feeValue && !validateFeeFormat(feeValue)) {
                    invalidFees.push(
                        `${clinicianEmail} - ${field
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (l) => l.toUpperCase())}`
                    );
                }
            });
        });
    }

    return {
        isValid: invalidFees.length === 0,
        invalidFees,
    };
};

interface ClinicianWithFeesData {
    clinician: { fullname: string; email: string; credential?: string };
    fees: IFeeSettingFields;
}

interface FeesFormProps {
    client: IClient;
    showLicFees: boolean;
    updateClientByStatus: TUpdateClientByStatus;
    feesCalculated?: IFeesCalculated | undefined;
    updateMultipleClientFieldsByStatus?: TUpdateMultipleClientFieldsByStatus;
    autoFillFees?: boolean;
    updateClient: TUpdateClient;
    onValidationChange?: (isValid: boolean) => void;
}

export default function FeesForm({
    client,
    feesCalculated,
    updateClientByStatus,
    updateMultipleClientFieldsByStatus,
    autoFillFees,
    updateClient,
    showLicFees,
    onValidationChange,
}: FeesFormProps) {
    const loggedInUser =
        React.useContext(AclContext)?.authUser.user?.displayName || "Intake team";
    const { addToast } = useToasts();

    const previousMastersFeeRef = React.useRef<string | null>(null);
    const previousDoctoralFeeRef = React.useRef<string | null>(null);

    const [cliniciansWithFees, setCliniciansWithFees] = React.useState<
        ClinicianWithFeesData[]
    >([]);

    const [showClinicianFeesDialog, setShowClinicianFeesDialog] =
        React.useState(false);

    React.useEffect(() => {
        const validation = validateAllFees(client);
        onValidationChange?.(validation.isValid);
    }, [client, onValidationChange]);

    const handleFeesChange = React.useCallback(() => {
        if (updateMultipleClientFieldsByStatus) {
            updateMultipleClientFieldsByStatus(ClientStatus.FEE_SETTING, {
                masters_fee: feesCalculated?.data?.[0]?.masters as string,
                dr_fee: feesCalculated?.data?.[0]?.doctoral as string,
            });
            updateClient("fees_update_details", {
                updated_by: loggedInUser,
                updated_at: new Date(),
                reason_for_fee_update:
                    client?.fees_update_details?.reason_for_fee_update || "",
            });
        }
    }, [
        client?.fees_update_details?.reason_for_fee_update,
        feesCalculated?.data,
        loggedInUser,
        updateClient,
        updateMultipleClientFieldsByStatus,
    ]);

    React.useEffect(() => {
        if (autoFillFees) {
            const currentMastersFee = feesCalculated?.data?.[0]?.masters;
            const currentDoctoralFee = feesCalculated?.data?.[0]?.doctoral;
            if (
                (currentMastersFee &&
                    previousMastersFeeRef.current !== currentMastersFee) ||
                (currentDoctoralFee &&
                    previousDoctoralFeeRef.current !== currentDoctoralFee)
            ) {
                handleFeesChange();
                previousMastersFeeRef.current = currentMastersFee as string;
                previousDoctoralFeeRef.current = currentDoctoralFee as string;
            }
        }
    }, [feesCalculated, handleFeesChange, autoFillFees]);

    const clinicianEmails = React.useMemo(() => {
        const emails = new Set<string>();

        client.confirmed_bookings?.forEach((booking) => {
            if (booking.clinician_id) {
                emails.add(booking.clinician_id);
            }
        });
        client.held_bookings?.forEach((booking) => {
            if (booking.clinician_id) {
                emails.add(booking.clinician_id);
            }
        });

        if (client.clinician_fees) {
            const overrideEmails = Object.keys(client.clinician_fees).filter(
                (email) => {
                    const fees = client.clinician_fees![email];
                    return (
                        fees &&
                        Object.keys(fees).some(
                            (key) => fees[key as keyof IFeeSettingFields]
                        )
                    );
                }
            );
            overrideEmails.forEach((email) => emails.add(email));
        }

        return Array.from(emails);
    }, [client.confirmed_bookings, client.held_bookings, client.clinician_fees]);

    const fetchCliniciansAndFees = React.useCallback(async () => {
        if (clinicianEmails.length === 0) {
            setCliniciansWithFees([]);
            return;
        }

        const fetched = await Promise.all(
            clinicianEmails.map(async (email) => {
                const clinician = await getClinicianByEmail(email);
                if (!clinician || !client.clinician_fees?.[email]) return null;

                return {
                    clinician: {
                        fullname: clinician.fullname,
                        email: clinician.email,
                        credential: clinician.credential,
                    },
                    fees: client.clinician_fees?.[email],
                } as ClinicianWithFeesData;
            })
        );
        setCliniciansWithFees(fetched.filter(Boolean) as ClinicianWithFeesData[]);
    }, [clinicianEmails, client.clinician_fees]);

    React.useEffect(() => {
        fetchCliniciansAndFees();
    }, [fetchCliniciansAndFees]);

    const updateGeneralFee = (
        field: keyof IFeeSettingFields | "is_self_pay",
        value: any
    ) => {
        updateClientByStatus(ClientStatus.FEE_SETTING, field as any, value as any);
        updateClient("fees_update_details", {
            updated_by: loggedInUser,
            updated_at: new Date(),
            reason_for_fee_update:
                client?.fees_update_details?.reason_for_fee_update || "",
        });
    };

    const handleSelfPayToggle = () => {
        const nextValue = !Boolean(client[ClientStatus.FEE_SETTING]?.is_self_pay);
        updateGeneralFee("is_self_pay", nextValue);
        if (!nextValue || !updateMultipleClientFieldsByStatus) {
            return;
        }
        const patch = getSelfPayInsurancePatch(client);
        if (Object.keys(patch).length === 0) {
            return;
        }
        updateMultipleClientFieldsByStatus(ClientStatus.INS_VERIFICATION, patch);
        const message = buildSelfPayAutoFillToastMessage(patch);
        if (message) {
            addToast(message, { appearance: "success", autoDismiss: true });
        }
    };

    const updateClinicianFee = (
        clinicianEmail: string,
        field: keyof IFeeSettingFields | "is_self_pay",
        value: string | boolean
    ) => {
        const current = client.clinician_fees || {};
        const forClinician = (current[clinicianEmail] || {}) as IFeeSettingFields;
        const updated = {
            ...current,
            [clinicianEmail]: {
                ...forClinician,
                [field]: value as any,
            },
        };

        // Generate calculated value for fee fields
        if (
            typeof value === "string" &&
            [
                "dr_fee",
                "dr_fee_a",
                "dr_fee_b",
                "dr_fee_c",
                "masters_fee",
                "inn_masters_fee",
                "inn_nutrition_fee",
                "psychiatry_fee",
                "psychiatry_fee_a",
                "psychiatry_fee_b",
                "psychiatry_fee_c",
                "intern_fee",
                "lic_fee",
                "kap_fee",
                "nutrition_fee",
                "lic_a_fee",
                "lic_b_fee",
                "lic_c_fee",
                "extern_fee",
                "career_fee",
                "gifted_assessment_fee",
                "short_term_evaluation_documentation_fee",
            ].includes(field)
        ) {
            // Determine department_fee_type based on the fee field
            const department_fee_type = [
                "inn_masters_fee",
                "inn_nutrition_fee",
            ].includes(field)
                ? "INN FEES"
                : "";

            const fee_calc = validateFees(
                value as string,
                field,
                department_fee_type
            );
            (updated[clinicianEmail] as any)[`${field}_calc`] = fee_calc;
        }

        updateClient("clinician_fees", updated);
        updateClient("fees_update_details", {
            updated_by: loggedInUser,
            updated_at: new Date(),
            reason_for_fee_update:
                client?.fees_update_details?.reason_for_fee_update || "",
        });
    };

    const onnFeeFields: Array<{ key: keyof IFeeSettingFields; label: string }> = [
        { key: "masters_fee" as keyof IFeeSettingFields, label: "Masters Fee" },
        ...(showLicFees
            ? [
                  { key: "lic_fee" as keyof IFeeSettingFields, label: "LIC Fee" },
                  {
                      key: "lic_a_fee" as keyof IFeeSettingFields,
                      label: "LIC A Fee",
                  },
                  {
                      key: "lic_b_fee" as keyof IFeeSettingFields,
                      label: "LIC B Fee",
                  },
                  {
                      key: "lic_c_fee" as keyof IFeeSettingFields,
                      label: "LIC C Fee",
                  },
              ]
            : []),
        { key: "dr_fee" as keyof IFeeSettingFields, label: "Dr Fee" },
        ...(showLicFees
            ? [
                  { key: "dr_fee_a" as keyof IFeeSettingFields, label: "Dr Fee A" },
                  { key: "dr_fee_b" as keyof IFeeSettingFields, label: "Dr Fee B" },
                  { key: "dr_fee_c" as keyof IFeeSettingFields, label: "Dr Fee C" },
              ]
            : []),
        { key: "psychiatry_fee" as keyof IFeeSettingFields, label: "Psych Fee" },
        ...(showLicFees
            ? [
                  {
                      key: "psychiatry_fee_a" as keyof IFeeSettingFields,
                      label: "Psych A Fee",
                  },
                  {
                      key: "psychiatry_fee_b" as keyof IFeeSettingFields,
                      label: "Psych B Fee",
                  },
                  {
                      key: "psychiatry_fee_c" as keyof IFeeSettingFields,
                      label: "Psych C Fee",
                  },
              ]
            : []),
        { key: "testing_fee" as keyof IFeeSettingFields, label: "Testing Fee" },
        {
            key: "intern_extern_testing_fee",
            label: "Intern/Extern Testing Fee",
        },
        { key: "nutrition_fee" as keyof IFeeSettingFields, label: "Nutrition Fee" },
        { key: "intern_fee" as keyof IFeeSettingFields, label: "Intern Fee" },
        { key: "extern_fee" as keyof IFeeSettingFields, label: "Extern Fee" },
        { key: "career_fee" as keyof IFeeSettingFields, label: "Career Fee" },
        {
            key: "gifted_assessment_fee" as keyof IFeeSettingFields,
            label: "Gifted Assessment Fee",
        },
        { key: "kap_fee" as keyof IFeeSettingFields, label: "Kap Fee" },
        {
            key: "short_term_evaluation_documentation_fee" as keyof IFeeSettingFields,
            label: "Short Term Evaluation & Documentation Fee",
        },
    ];

    const innFeeFields: Array<{ key: keyof IFeeSettingFields; label: string }> = [
        { key: "inn_masters_fee" as keyof IFeeSettingFields, label: "Masters Fee" },
        {
            key: "inn_nutrition_fee" as keyof IFeeSettingFields,
            label: "Nutrition Fee",
        },
    ];

    const toColumnKey = (email: string) =>
        `clinician_${email.replace(/[^a-zA-Z0-9_]/g, "_")}`;

    // Build columns for ONN table
    const onnColumns = [
        { Header: "Fee Type", accessor: "feeType" },
        { Header: "General", accessor: "general" },
        ...cliniciansWithFees.map((c) => {
            const id = toColumnKey(c.clinician.email);
            return {
                Header: c.clinician.fullname,
                id,
                accessor: (row: any) => row[id],
            } as any;
        }),
    ];

    const onnData = [
        (() => {
            const row: any = {
                feeType: <span>Self-Pay</span>,
                general: (
                    <FormCheckbox
                        variant="small"
                        label="Client is Self-Pay"
                        checked={Boolean(
                            client[ClientStatus.FEE_SETTING]?.is_self_pay
                        )}
                        onChange={handleSelfPayToggle}
                        wrapperClass={cn({
                            [styles.selfPayCheckbox]: Boolean(
                                client[ClientStatus.FEE_SETTING]?.is_self_pay
                            ),
                        })}
                        labelClass={cn({
                            [styles.selfPayCheckboxLabel]: Boolean(
                                client[ClientStatus.FEE_SETTING]?.is_self_pay
                            ),
                        })}
                    />
                ),
            };

            cliniciansWithFees.forEach((c) => {
                const key = toColumnKey(c.clinician.email);
                const isChecked = Boolean(c.fees?.is_self_pay);

                row[key] = (
                    <FormCheckbox
                        variant="small"
                        label="Client is Self-Pay"
                        checked={isChecked}
                        onChange={() =>
                            updateClinicianFee(
                                c.clinician.email,
                                "is_self_pay",
                                !isChecked
                            )
                        }
                        wrapperClass={cn({
                            [styles.selfPayCheckbox]: isChecked,
                        })}
                        labelClass={cn({
                            [styles.selfPayCheckboxLabel]: isChecked,
                        })}
                    />
                );
            });

            return row;
        })(),
        ...onnFeeFields.map((field) => {
            const row: any = {
                feeType: field.label,
                general: (
                    <FeeInputWithTooltip
                        value={
                            (client[ClientStatus.FEE_SETTING]?.[
                                field.key
                            ] as string) || ""
                        }
                        onChange={(value) => updateGeneralFee(field.key, value)}
                        calcValue={
                            client[ClientStatus.FEE_SETTING]?.[
                                field.key + "_calc"
                            ] as string
                        }
                        skipValidation={
                            field.key === "testing_fee" ||
                            field.key === "intern_extern_testing_fee"
                        }
                    />
                ),
            };

            cliniciansWithFees.forEach((c) => {
                const key = toColumnKey(c.clinician.email);
                const value = (c.fees?.[field.key] as string) || "";

                row[key] = (
                    <FeeInputWithTooltip
                        value={value}
                        onChange={(value) =>
                            updateClinicianFee(c.clinician.email, field.key, value)
                        }
                        calcValue={c.fees?.[field.key + "_calc"] as string}
                        skipValidation={
                            field.key === "testing_fee" ||
                            field.key === "intern_extern_testing_fee"
                        }
                    />
                );
            });
            return row;
        }),
    ];

    const innColumns = [
        { Header: "Fee Type", accessor: "feeType" },
        { Header: "General", accessor: "general" },
        ...cliniciansWithFees.map((c) => {
            const id = toColumnKey(c.clinician.email);
            return {
                Header: c.clinician.fullname,
                id,
                accessor: (row: any) => row[id],
            } as any;
        }),
    ];

    const innData = innFeeFields.map((field) => {
        const row: any = {
            feeType: field.label,
            general: (
                <FeeInputWithTooltip
                    value={
                        (client[ClientStatus.FEE_SETTING]?.[field.key] as string) ||
                        ""
                    }
                    onChange={(value) => updateGeneralFee(field.key, value)}
                    calcValue={
                        client[ClientStatus.FEE_SETTING]?.[
                            field.key + "_calc"
                        ] as string
                    }
                />
            ),
        };
        cliniciansWithFees.forEach((c) => {
            const key = toColumnKey(c.clinician.email);
            const value = (c.fees?.[field.key] as string) || "";

            row[key] = (
                <FeeInputWithTooltip
                    value={value}
                    onChange={(value) =>
                        updateClinicianFee(c.clinician.email, field.key, value)
                    }
                    calcValue={c.fees?.[field.key + "_calc"] as string}
                />
            );
        });

        return row;
    });

    return (
        <>
            <div className={styles.feesForm}>
                {cliniciansWithFees.length > 0 && (
                    <Alert type="info">
                        <strong>Note:</strong> Clinician-specific fees override the
                        General fees for that clinician's sessions. Leave a field
                        blank to use the General fee.
                    </Alert>
                )}
                <div className={styles.clinicianButtonContainer}>
                    <p className="sectionTitle">ONN Fees </p>
                    <div className={styles.clinicianButton}>
                        {((client.confirmed_bookings &&
                            client.confirmed_bookings.length > 0) ||
                            (client.held_bookings &&
                                client.held_bookings.length > 0)) && (
                            <Button
                                onClick={() => {
                                    setShowClinicianFeesDialog(true);
                                }}
                                variant="primary"
                                mode="outline"
                                iconLeft={<PlusCircleIcon />}
                            >
                                Set Fees Per Clinician
                            </Button>
                        )}
                        <ClinicianFeesDialog
                            show={showClinicianFeesDialog}
                            onHide={() => setShowClinicianFeesDialog(false)}
                            client={client}
                            updateClient={updateClient}
                        />
                    </div>
                </div>

                <Table
                    variant="compact"
                    allowSelect={false}
                    columns={onnColumns}
                    data={onnData}
                />

                <div className={styles.feeUpdateReason}>
                    <TextArea
                        label="Reason for fee update"
                        placeholder="Please fill this out if you are updating the fees for a client."
                        value={
                            client?.fees_update_details?.reason_for_fee_update || ""
                        }
                        onChange={(e) => {
                            updateClient("fees_update_details", {
                                updated_by: loggedInUser,
                                updated_at: new Date(),
                                reason_for_fee_update: e.target.value,
                            });
                        }}
                    />
                </div>
            </div>

            <div className={styles.feesForm}>
                <p className="sectionTitle">INN Fees</p>
                <Table
                    variant="compact"
                    allowSelect={false}
                    columns={innColumns}
                    data={innData}
                />
            </div>
        </>
    );
}
